import React, { useState } from 'react'
import { styled } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import Box from '@mui/material/Box'
import Snackbar from '@mui/material/Snackbar'
import MuiAlert, { AlertProps } from '@mui/material/Alert'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { useDelegateRecipient } from 'proposals/proposal/queries/useProposalMutations'

const PREFIX = 'ProposalDelegate'

const classes = {
  proposalDelegate: `${PREFIX}-proposalDelegate`,
  buttonContainer: `${PREFIX}-buttonContainer`
}

const Root = styled('div')(({ theme: { breakpoints, spacing, shape } }) => ({
  [`& .${classes.proposalDelegate}`]: {
    width: '100%',
    borderRadius: shape.borderRadius,
    backgroundColor: '#EFEFEF',
    padding: spacing(2),
    marginTop: spacing(2),
    [breakpoints.up('md')]: {
      maxWidth: 443
    }
  },

  [`& .${classes.buttonContainer}`]: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: spacing(2)
  }
}))

type ProposalDelegatePropTypes = {
  rfpId: string
  token: string
}

const Alert = (props: AlertProps) => <MuiAlert elevation={6} variant="filled" {...props} />

const ProposalDelegate: React.FC<ProposalDelegatePropTypes> = ({ rfpId, token }) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [email, setEmail] = useState('')

  const handleClose = (_event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return
    }

    setOpen(false)
  }

  const { mutate: sendDelegate, isError, isPending, reset } = useDelegateRecipient(rfpId, token, () => {
    setOpen(true)
    setEmail('')
    queryClient.invalidateQueries({ queryKey: ['rfp', token] })
    queryClient.invalidateQueries({ queryKey: ['proposal-state', token] })
  })

  return (
    <Root>
      <Box displayPrint="none">
        <article className={classes.proposalDelegate}>
          <header>
            <Typography paragraph>{t('delegate-request-for-proposal')}</Typography>
          </header>
          <Typography variant="body2" gutterBottom>
            {t('delegate-request-for-proposal-text')}
          </Typography>
          <TextField
            fullWidth
            variant="filled"
            type="email"
            label={t('delegate-email')}
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
          <footer className={classes.buttonContainer}>
            <Button
              color="grey"
              variant="outlined"
              onClick={() => sendDelegate(email.trim())}
              disabled={email.trim() === '' || isPending}
            >
              {t('delegate-submit')}
            </Button>
          </footer>
        </article>
      </Box>
      <Snackbar
        open={open}
        autoHideDuration={3000}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <div>
          <Alert onClose={handleClose} severity="success">
            {t('delegate-success')}
          </Alert>
        </div>
      </Snackbar>
      <Snackbar
        open={isError}
        autoHideDuration={4000}
        onClose={() => reset()}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <div>
          <Alert onClose={() => reset()} severity="error">
            {t('delegate-error')}
          </Alert>
        </div>
      </Snackbar>
    </Root>
  )
}

export default ProposalDelegate
